import React, { useState, useContext, Fragment } from "react";
import moment from "moment";
import Uri from "jsuri";

import { AppContext } from "./../../contexts/AppContextProvider";

import InsightArchive from "./InsightArchive";
import InsightsSummary from "./InsightsSummary";
import Heading from "./../Elements/Heading";
import Loader from "./../Loader/Loader.js";
import { BASEURL, HEADERS } from "./../Utils/Constants";

import useInfiniteScroll from "./../Hooks/useInfiniteScroll";

const InsightsArchiveList = (props) => {
  const { state } = useContext(AppContext);
  const data = props.data;

  const [insights, setInsights] = useState(
    data.data.length > 2 ? data.data.filter((e, i) => i > 1) : []
  );
  const [page, setPage] = useState(1);
  const [moreData, setMoreData] = useState(props.data.moreData);
  const [error, setError] = useState(false);

  // Account for generation time
  const yesterday = moment().subtract(1, "days");

  const yesterdaysInsights = data.data[1]
    ? yesterday.isSame(data.data[1].date, "d")
    : null;

  const [isFetching, setIsFetching] = useInfiniteScroll(fetchData);

  async function fetchData() {
    if (moreData) {
      setIsFetching(true);

      let insightsConfigURL = new Uri(
        state.currentProject.projectObj.insightsConfig.feed.url
      );

      const versionParam = localStorage.configVersion || "production";
      insightsConfigURL.addQueryParam("page", page);
      insightsConfigURL
        .deleteQueryParam("version")
        .addQueryParam("version", versionParam);

      // URL overwrites
      const thresholdParam = new Uri(window.location.search).getQueryParamValue(
        "threshold"
      );

      if (thresholdParam) {
        insightsConfigURL = insightsConfigURL.replaceQueryParam(
          "threshold",
          thresholdParam
        );
      }

      let url = `${BASEURL}${insightsConfigURL}`;

      let response = await fetch(url, {
        method: "GET",
        headers: HEADERS(window.localStorage.token),
      });

      if (!response.ok) {
        setIsFetching(false);
        setError(true);
      } else {
        let insightsData = await response.json();
        setIsFetching(false);

        // get a new list to avoid mutation (it does mutate when sorting)
        const sortedElements = [...insightsData.data].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

        // get the last element from the current list of insights...
        const lastElementDate = sortedElements.length
          ? sortedElements[0].date
          : null;

        // do we have some insight with the same date already inserted?
        const shouldRequestMoreData = lastElementDate
          ? insights.find((e) => e.date === lastElementDate) === undefined
          : false;

        if (insightsData.data.length > 0 && shouldRequestMoreData) {
          setInsights((prevState) => [...prevState, ...insightsData.data]);
          setPage((prevPageState) => prevPageState + 1);
        }
        setMoreData(
          insightsData.moreData &&
            shouldRequestMoreData &&
            insightsData.data.length
        );
        setError(false);
      }
    } else {
      setMoreData(false);
    }
  }

  const renderInsights = (data) =>
    data.map((insights, i) => (
      <Fragment key={i + 1}>
        <InsightArchive {...insights} />
        {i !== data.length - 1 && <hr className="insight-archive__hr mb-4" />}
      </Fragment>
    ));

  const renderSummaryInsights = (data) =>
    data.map((insights, i) =>
      insights.data.map((data, i) => {
        return <InsightsSummary key={i + 2} {...data} />;
      })
    );

  const renderInsightsOnScroll = (data) =>
    data.map((group, i) => (
      <div
        key={i + 4}
        className="insights-rest insights-archive-container pt-3 animated fadeIn"
      >
        <Heading
          size={5}
          className={"insight-archive__title insight-archive__title--flex pt-1"}
        >
          {moment.utc(data[i].date).format("dddd, MMM DD")}
        </Heading>

        <div className="insights-wrapper">
          {group.data.map((item, i) => (
            <Fragment key={i + 5}>
              <InsightArchive {...item} text_size={4} />
              {i !== group.data.length - 1 && (
                <hr className="insight-archive__hr mb-4" />
              )}
            </Fragment>
          ))}
        </div>
      </div>
    ));

  return (
    <Fragment>
      {data && data.data[0] && (
        <div className="insights-recent insights-archive-container">
          <Heading
            size={3}
            className={"insight-archive__title insight-archive__title--flex"}
          >
            Recent Insights
          </Heading>
          <div className="insights-wrapper">
            {data.data[0] && renderInsights(data.data[0].data)}
          </div>
        </div>
      )}

      {data && data.data_summary && (
        <div className="insights-summary">
          <Heading size={3} className={"insight-archive__title mb-4 mt-3"}>
            Top 5 KPI Summary for Yesterday:
          </Heading>
          {renderSummaryInsights(data.data_summary)}
        </div>
      )}

      {data && data.data[1] && (
        <div className="insights-rest insights-archive-container pt-3">
          <Heading
            size={4}
            className={"insight-archive__title insight-archive__title--flex"}
          >
            {yesterdaysInsights ? "Yesterday" : "Two Days Ago"}
          </Heading>
          <div className="insights-wrapper">
            {renderInsights(data.data[1].data)}
          </div>
        </div>
      )}

      {renderInsightsOnScroll(insights)}

      {moreData && isFetching && (
        <Loader minHeight={200} col={"col-12"} size="circle-scale--sm" />
      )}

      {!moreData && (
        <div className="alert alert-warning mt-5" role="alert">
          There are no more insights.
        </div>
      )}

      {error && (
        <div className="alert alert-danger mt-5" role="alert">
          There was an error loading insights. Please refresh the page.
        </div>
      )}
    </Fragment>
  );
};

export default InsightsArchiveList;
